import React, { useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";

import config from "../../../../config";
import useInterventionTable from "./useInterventionTable";

const InterventionTable = ({ setErrorSB, setSuccessSB }) => {
  const navigate = useNavigate();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [counter, setCounter] = useState(0);
  const [actionsOpen, setActionsOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const { tableData, loading, error } = useInterventionTable(counter);

  const openActions = (row) => {
    setSelectedRow(row);
    setActionsOpen(true);
  };

  const closeActions = () => {
    setActionsOpen(false);
    setSelectedRow(null);
  };

  const openDeleteConfirm = () => {
    setActionsOpen(false);
    setOpenDeleteDialog(true);
  };

  const handleDownloadPdf = async () => {
    if (!selectedRow?.id) return;
    try {
      const response = await axios.get(
        `${config.MAP_MATCHING_API_URL}/interventions/${selectedRow.id}/file`,
        {
          headers: { "X-API-Key": config.MAP_MATCHING_API_TOKEN },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", selectedRow.file?.fileName || `intervention-${selectedRow.id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download PDF:", err);
      if (setErrorSB) setErrorSB(true);
    }
  };

  const closeDeleteConfirm = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedRow?.id) return;

    setDeleting(true);
    try {
      await axios.delete(`${config.MAP_MATCHING_API_URL}/interventions/${selectedRow.id}`, {
        headers: {
          "Content-Type": "application/json",
          "X-API-Key": `${config.MAP_MATCHING_API_TOKEN}`,
        },
      });

      setCounter((currentCounter) => currentCounter + 1);

      if (setSuccessSB) {
        setSuccessSB(true);
      }
    } catch (deleteError) {
      console.error("Failed to delete intervention:", deleteError);
      if (setErrorSB) {
        setErrorSB(true);
      }
    } finally {
      setDeleting(false);
      setOpenDeleteDialog(false);
      setSelectedRow(null);
    }
  };

  const dialogPaperSx = (theme) => ({
    backgroundColor: darkMode ? theme.palette.background.card : theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 2,
    minWidth: { xs: "90vw", sm: 420 },
    border: `1px solid ${
      darkMode ? "rgba(255,255,255,0.12)" : "rgba(52,71,103,0.12)"
    }`,
    boxShadow: theme.shadows[darkMode ? 24 : 8],
  });

  const dialogDividerColor = darkMode ? "rgba(255,255,255,0.12)" : "rgba(52,71,103,0.12)";

  if (loading) {
    return (
      <div className="rawContainerCenteredH">
        <CircularProgress />
      </div>
    );
  }

  if (error) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h6" color="error" gutterBottom>
          Error: {error}
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <div className="diagramContainer">
      <div className="scrollable-100">
        <DataTable table={tableData} canSearch onRowClick={(row) => openActions(row)} />
      </div>

      <Dialog
        open={actionsOpen}
        onClose={closeActions}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle>Ενέργειες</DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Επιλέξτε ενέργεια για την παρέμβαση #{selectedRow?.id}
          </MDTypography>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, gap: 1, flexWrap: "wrap" }}>
          <MDButton
            variant="outlined"
            color="info"
            onClick={() => {
              if (!selectedRow?.id) return;
              navigate(`/list/interventions/${selectedRow.id}`);
              closeActions();
            }}
            sx={{ "&.MuiButton-root": { textTransform: "none" } }}
          >
            Προβολή
          </MDButton>

          <MDButton
            variant="contained"
            color="info"
            onClick={() => {
              if (!selectedRow?.id) return;
              navigate(`/list/interventions/${selectedRow.id}/edit`);
              closeActions();
            }}
            sx={{ "&.MuiButton-root": { textTransform: "none" } }}
          >
            Επεξεργασία
          </MDButton>

          {selectedRow?.file && (
            <MDButton
              variant="contained"
              color="warning"
              onClick={() => { handleDownloadPdf(); closeActions(); }}
              sx={{ "&.MuiButton-root": { textTransform: "none" } }}
            >
              PDF
            </MDButton>
          )}

          <MDButton
            variant="contained"
            color="error"
            onClick={openDeleteConfirm}
            sx={{ "&.MuiButton-root": { textTransform: "none" } }}
          >
            Διαγραφή
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={closeDeleteConfirm}
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${dialogDividerColor}`,
          }}
        >
          Επιβεβαίωση Διαγραφής
        </DialogTitle>

        <DialogContent>
          <MDTypography variant="button" color="text">
            Θέλετε σίγουρα να διαγράψετε την παρέμβαση #{selectedRow?.id};
          </MDTypography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${dialogDividerColor}`,
          }}
        >
          <MDButton
            variant="outlined"
            color="info"
            onClick={closeDeleteConfirm}
            disabled={deleting}
            sx={(theme) => ({
              "&.MuiButton-root": { textTransform: "none" },
              color: theme.palette.text.primary,
              borderColor: darkMode ? "rgba(255,255,255,0.35)" : "rgba(52,71,103,0.3)",
              "&:hover": {
                borderColor: theme.palette.text.primary,
              },
            })}
          >
            Ακύρωση
          </MDButton>

          <MDButton
            variant="contained"
            color="error"
            onClick={handleDeleteConfirmed}
            disabled={deleting}
            sx={{ "&.MuiButton-root": { textTransform: "none" } }}
          >
            {deleting ? "Διαγραφή..." : "Διαγραφή"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InterventionTable;
